import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Brain, Activity, Scale, HeartPulse, Stethoscope, ArrowRight } from 'lucide-react';
export default function Services() {
  const services = [{
    title: 'Mental Capacity Assessments UK',
    description: "Meticulous evaluations of an individual's capacity to make specific decisions, adhering to the Mental Capacity Act 2005. These assessments are vital for upholding individual rights and ensuring legal decisions are based on a profound understanding of mental capacity.",
    icon: <Activity className="h-12 w-12" data-id="element-965" />,
    link: '/services/mental-capacity'
  }, {
    title: 'PTSD & Trauma Psychiatric Evaluations',
    description: "Expert assessments for Post-Traumatic Stress Disorder (PTSD) and Complex PTSD. Dr. Syed's reports adhere to international standards, providing sensitive and comprehensive evaluations of psychological distress stemming from traumatic experiences.",
    icon: <HeartPulse className="h-12 w-12" data-id="element-966" />,
    link: '/services/trauma-ptsd'
  }, {
    title: 'ADHD & Autism Medico-Legal Assessments',
    description: "Comprehensive assessments for Attention-Deficit/Hyperactivity Disorder (ADHD) and Autism Spectrum Disorder (ASD). These insights are crucial in understanding how neurodevelopmental conditions may impact an individual's legal context or capacity.",
    icon: <Brain className="h-12 w-12" data-id="element-967" />,
    link: '/services/neurodevelopmental'
  }, {
    title: 'Forensic Psychiatric Assessments UK',
    description: 'Critical insights into mental state within criminal justice contexts, including assessments regarding fitness to plead and stand trial, alongside thorough risk assessments concerning violence, self-harm, and self-neglect.',
    icon: <Scale className="h-12 w-12" data-id="element-968" />,
    link: '/services/forensic-risk'
  }, {
    title: 'Psychiatric Reports for Courts & Tribunals',
    description: 'Expert psychiatric opinions tailored for diverse legal settings, including Immigration and Asylum Tribunals, Criminal Courts, Family Courts, Employment Tribunals, Civil Claims, and Mental Health Review Tribunals.',
    icon: <Stethoscope className="h-12 w-12" data-id="element-969" />,
    link: '/services/specialist'
  }];
  return <main className="min-h-screen bg-white" data-id="element-970">
      <Helmet data-id="element-971">
        <title data-id="element-972">
          Medico-Legal Psychiatry Services – Expert Psychiatric Insights
        </title>
        <meta name="description" content="Specialist medico-legal psychiatric services: mental capacity, forensic, PTSD, ADHD/ASD & court-ready expert witness reports." data-id="element-973" />
        <meta name="keywords" content="Medico-Legal Psychiatry, Expert Witness Psychiatrist UK, Psychiatric Expert Witness Reports, Independent Psychiatric Reports UK, Forensic Psychiatric Assessments UK, Mental Capacity Assessments UK, ADHD & Autism Medico-Legal Assessments, PTSD & Trauma Psychiatric Evaluations, Psychiatric Reports for Courts & Tribunals, Psychiatric Expert for Solicitors and Barristers" data-id="element-974" />
        <script type="application/ld+json" data-id="element-975">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "Medico-Legal Psychiatry Services",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "Psychiatric Expert Witness Services",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert Psychiatric Reports and Assessments"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10" data-id="element-976">
        <div className="container mx-auto px-4" data-id="element-977">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-4 text-center" data-id="element-978">
            Medico-Legal Psychiatry Services – Expert Psychiatric Insights
          </h1>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-979">
            Dr. Syed provides comprehensive Medico-Legal services for
            solicitors, barristers, and courts, guided by clarity, compassion,
            and expertise.
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12" data-id="element-980">
        <div className="container mx-auto px-4" data-id="element-981">
          <div className="max-w-3xl mx-auto mb-12" data-id="element-982">
            <p className="text-slate-dark text-lg mb-6" data-id="element-983">
              At Dr. Imtiaz Syed's practice, we are dedicated to providing
              precise, impartial, and robust psychiatric expertise to legal
              professionals across the UK. We understand the critical role that
              expert insights play in complex legal proceedings, and our
              services are meticulously designed to deliver clarity and
              confidence to your cases.
            </p>
            <p className="text-slate-dark text-lg mb-6" data-id="element-984">
              Dr. Syed offers a comprehensive suite of medico-legal psychiatric
              assessments and expert witness services, all delivered with the
              guiding principles of Clarity, Compassion, and Expertise. Each
              assessment is conducted with the utmost care, ensuring
              thoroughness and sensitivity, leading to reports that are not only
              scientifically sound but also practical and directly applicable to
              legal contexts.
            </p>
            <p className="text-slate-dark text-lg" data-id="element-985">
              Explore our key areas of specialisation:
            </p>
          </div>
          {/* Services */}
          <div className="space-y-12 mb-16" data-id="element-986">
            {services.map((service, index) => <div key={index} className="bg-white rounded-lg shadow-md p-8 border-l-4 border-primary" data-id="element-987">
                <div className="flex flex-col md:flex-row gap-6" data-id="element-988">
                  <div className="text-primary md:w-24 flex justify-center" data-id="element-989">
                    {service.icon}
                  </div>
                  <div className="flex-1" data-id="element-990">
                    <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-991">
                      {service.title}
                    </h2>
                    <p className="text-slate-dark mb-6" data-id="element-992">
                      {service.description}
                    </p>
                    <Link to={service.link} className="inline-flex items-center text-primary font-medium hover:text-secondary transition-colors" data-id="element-993">
                      More on {service.title}{' '}
                      <ArrowRight className="ml-1 h-4 w-4" data-id="element-994" />
                    </Link>
                  </div>
                </div>
              </div>)}
          </div>
          {/* Why Choose Section */}
          <div className="bg-gray-50 rounded-lg p-8 shadow-md" data-id="element-995">
            <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-996">
              Why Choose Dr. Imtiaz Syed for Expert Witness Services?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-997">
              Our commitment is to deliver expert medico-legal reports that are:
            </p>
            <ul className="space-y-3 mb-6 text-slate-dark" data-id="element-998">
              <li className="flex items-start" data-id="element-999">
                <span className="text-secondary font-bold mr-2" data-id="element-1000">•</span>
                <span data-id="element-1001">
                  <strong data-id="element-1002">Robust & Objective:</strong> Clinically sound,
                  evidence-based, and impartial.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1003">
                <span className="text-secondary font-bold mr-2" data-id="element-1004">•</span>
                <span data-id="element-1005">
                  <strong data-id="element-1006">Clear & Concise:</strong> Jargon-free and directly
                  relevant to legal questions.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1007">
                <span className="text-secondary font-bold mr-2" data-id="element-1008">•</span>
                <span data-id="element-1009">
                  <strong data-id="element-1010">Timely & Responsive:</strong> Delivered efficiently to
                  meet court deadlines.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1011">
                <span className="text-secondary font-bold mr-2" data-id="element-1012">•</span>
                <span data-id="element-1013">
                  <strong data-id="element-1014">Ethical & Confidential:</strong> Adhering to the
                  highest professional standards.
                </span>
              </li>
            </ul>
            <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1015">
              Ready to Instruct Dr. Syed?
            </h3>
            <p className="text-slate-dark mb-4" data-id="element-1016">
              We invite legal professionals to explore these service areas in
              more detail via the dedicated subpages or to contact us directly
              to discuss your specific case requirements.
            </p>
            <div className="flex items-center" data-id="element-1017">
              <span className="mr-2 text-slate-dark" data-id="element-1018">Contact Us:</span>
              <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1019">
                enquiries@medicolegal-psychiatry.co.uk
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>;
}